import type { CustomerFeeCorrectionBody, CustomerFeeCorrectionResponse } from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchCustomerFeesApi } from "../../../../../api/client";
import { isHttpError } from "../../../../../lib/query/errors";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { dscWorkbenchKeys } from "../../../api/dscWorkbenchKeys";

/**
 * Mutation hook for saving customer fee corrections against a deal. Backed by
 * `WorkbenchCustomerFeesApi.correctCustomerFees` (PUT), which forwards the
 * `{ lineItemPk, correctedAmount }` lines to deal-fulfillment-service under the
 * deal's `expectedVersion`. On success the dashboard is invalidated so the
 * itemization's Total Customer Fees (and the fee lines) refresh from the server.
 * A stale version (409) also refreshes the dashboard and is left for the caller to message.
 * @param {string} approvalId - The deal / approval identifier.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSaveCustomerFees(approvalId: string) {
  const queryClient = useQueryClient();
  const shouldForceExit = useForceExitStore((state) => state.shouldForceExit);

  return useMutation({
    mutationFn: async (body: CustomerFeeCorrectionBody): Promise<CustomerFeeCorrectionResponse | undefined> => {
      if (shouldForceExit) {
        return undefined;
      }
      const response = await workbenchCustomerFeesApi.correctCustomerFees(approvalId, body);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: dscWorkbenchKeys.detail({ approvalId }) }),
    // A stale version means the fees changed under us: re-read so a retry carries the new token.
    onError: (error) =>
      isHttpError(error, 409)
        ? queryClient.invalidateQueries({ queryKey: dscWorkbenchKeys.detail({ approvalId }) })
        : undefined,
  });
}
